import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, idToString } from '@/helpers/mongoHelpers'

export default function useQuestionTypeList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { getItems, updateItem } = useLambda()
  const userData = store.state?.userStore?.userData
  const clientId = userData.role !== 'admin' ? userData.client.$oid : null
  const questionTypes = ref([])
  const collection = 'question_type'

  // Table Headers
  const tableColumns = computed(() => [
    { key: 'name', label: i18n.t('message.tableHeader.name'), sortable: true },
    { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
  ])

  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('name')
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  const fetchQuestionTypes = async () => {
    isLoading.value = true

    try {
      const query = {
        client_id: oid(clientId),
        deleted: { $ne: true },
      }

      const items = await getItems({ collection, query })

      questionTypes.value = items || []
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.question_type_fetch_error') })
    } finally {
      isLoading.value = false
    }
  }

  const filteredQuestionTypes = computed(() => {
    if (!searchQuery.value) return questionTypes.value
    const search = searchQuery.value.toLowerCase()
    return questionTypes.value.filter(e => e.name?.toLowerCase().includes(search))
  })

  const totalQuestionTypes = computed(() => filteredQuestionTypes.value.length)

  const deleteQuestionType = async (id) => {
    try {
      const typeId = idToString(id)
      const query = { _id: oid(typeId) }
      const action = { $set: { deleted: true } }

      await updateItem({ collection, query, action })

      // Unlink the question type from every behaviour that has it assigned
      const linkedProcesses = await getItems({
        collection: 'process',
        query: {
          client_id: oid(clientId),
          deleted: { $ne: true },
        },
        options: { projection: { activities: 1 } },
      })

      for (const processItem of (linkedProcesses || [])) {
        const activities = JSON.parse(JSON.stringify(processItem.activities || []))
        let changed = false
        activities.forEach(activity => {
          activity.behaviours?.forEach(behaviour => {
            if (!Array.isArray(behaviour.question_type)) return
            const remaining = behaviour.question_type.filter(q => idToString(q) !== typeId)
            if (remaining.length !== behaviour.question_type.length) {
              if (remaining.length) behaviour.question_type = remaining.map(q => oid(idToString(q)))
              else delete behaviour.question_type
              changed = true
            } else {
              behaviour.question_type = remaining.map(q => oid(idToString(q)))
            }
          })
        })
        if (!changed) continue

        // eslint-disable-next-line no-await-in-loop
        await updateItem({
          collection: 'process',
          query: { _id: oid(idToString(processItem._id)) },
          action: { $set: { activities } },
        })
      }

      fetchQuestionTypes()
      showSuccessMessage(i18n.t('message.question_type_remove'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.question_type_remove_error'))
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (filteredQuestionTypes.value.length ? 1 : 0)
    const to = Math.min(filteredQuestionTypes.value.length, currentPage.value * perPage.value)

    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalQuestionTypes.value })
  })

  watch(searchQuery, () => {
    currentPage.value = 1
  })

  return {
    fetchQuestionTypes,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    totalQuestionTypes,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    filteredQuestionTypes,
    deleteQuestionType,
    isLoading,
  }
}
